<template>
    <div
        :class="$style.initScreen"
        data-interface-target="init_screen"
    >
        <div
            :class="$style.blurBg"
            :style="{ '--bg': `url(${props.game.preview})` }"
        />
        <div :class="$style.content">
            <div :class="$style.imageAndButton">
                <img
                    :class="$style.image"
                    :src="props.game.preview"
                    :alt="props.game.title"
                    fetchpriority="high"
                />
                <Button
                    interfaceTarget="init_button"
                    effect="bevel"
                    visual="fill"
                    size="l"
                    iconLeft="play"
                    :class="$style.initButton"
                >
                    {{ $tr('gameContainer.init_button') }}
                </Button>
            </div>
            <div :class="$style.texts">
                <Typography
                    is="h1"
                    type="header"
                    size="s"
                >
                    {{ props.game.title }}
                </Typography>
                <Typography
                    is="div"
                    type="paragraph"
                    size="xs"
                >
                    <span :class="$style.categoriesLabel"> {{ $tr('gameDetails.categories') }}: </span>
                    <template
                        v-for="(category, index) in gameCategories"
                        :key="category.id"
                    >
                        <span> {{ category.title }} </span>{{ index !== gameCategories.length - 1 ? ', ' : '' }}
                    </template>
                </Typography>
                <div :class="$style.divider" />
                <Typography
                    is="div"
                    type="paragraph"
                    size="xs"
                    :class="$style.seoText"
                >
                    <SeoTextRaw :content="props.game.seo_text" />
                </Typography>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import Button from '@/components_new/Button.vue'
import SeoTextRaw from '@/components_new/SeoText/SeoTextRaw.vue'
import Typography from '@/components_new/Typography.vue'
import { APIGameType, GameCategoriesInfoType } from '@/types'

interface ExportInitScreenProps {
    game: APIGameType
    categories?: GameCategoriesInfoType
}

const props = defineProps<ExportInitScreenProps>()
const gameCategories = computed(
    () => props.categories?.categoriesInfo.filter((cat) => cat.categoryType === 'category') || [],
)
</script>
<style module>
.initScreen {
    display: grid;
    grid-template-rows: 1fr auto;
}

.blurBg,
.content {
    grid-area: 1/1;
}

.blurBg {
    background:
        linear-gradient(0deg, rgba(0, 0, 0, 0.48) 0%, rgba(0, 0, 0, 0.48) 100%),
        var(--bg) lightgray center / cover no-repeat;
    filter: blur(26px);
    transform: scale(1.1);
}

.content {
    padding: 0 8px;
    margin: 0 auto;
    position: relative;
    height: 100svh;
    box-sizing: border-box;
    mask-image: linear-gradient(black 0%, black 80%, transparent 96%);
}

.imageAndButton {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 30vh;
    margin-bottom: 32px;
}

.image {
    width: 100%;
    border-radius: var(--radius-16);
    aspect-ratio: var(--thumbnail-aspect-ratio);
    object-fit: cover;
}

.texts {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.categoriesLabel {
    opacity: 0.4;
}

.divider {
    background: var(--white-08);
    height: 1px;
}

.seoText {
    opacity: 0.8;
}

.seoText h2,
.seoText h3,
.seoText h4 {
    font-size: inherit;
    margin-bottom: 4px;
}

@media (--mobile-landscape), (--tablet-plus) {
    .content {
        max-width: 732px;
        padding: 0 34px;
    }

    .imageAndButton {
        flex-direction: row;
    }

    .imageAndButton > * {
        width: 50%;
    }

    .initButton {
        height: auto;
        align-self: stretch;
    }
    .initButton span {
        font-size: 26px;
    }
}
</style>
